import React, { useState, useEffect, useCallback, useMemo } from "react";
import { counterpartyApi } from "../api/api";
import { Request } from "../types";
import { logger } from "../utils/logger";
import { formatInTimeZone } from "date-fns-tz";
import RejectRequestWidget from "./RejectRequestWidget";
import ConfirmTakeWidget from "./ConfirmTakeWidget";
import ConfirmApproveWidget from "./ConfirmApproveWidget";

const RequestsTable: React.FC = () => {
  const [requests, setRequests] = useState<Request[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectWidgetOpen, setRejectWidgetOpen] = useState(false);
  const [selectedRequestForReject, setSelectedRequestForReject] = useState<Request | null>(null);
  const [confirmTakeOpen, setConfirmTakeOpen] = useState(false);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [isTakingRequest, setIsTakingRequest] = useState(false);
  const [isApprovingRequest, setIsApprovingRequest] = useState(false);
  // const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const requestsPerPage = 14;

  // console.log('RequestsTable: Component mounted');

  const fetchRequests = useCallback(async () => {
    try {
      // console.log('RequestsTable: fetchRequests called');
      setLoading(true);
      setError(null);
      const response = await counterpartyApi.getRequests();
      // console.log('RequestsTable: API response:', response);
      setRequests(response);
    } catch (err: unknown) {
      // console.error('RequestsTable: Error fetching requests:', err);
      logger.error("Error fetching requests:", err);
      setError("Ошибка при получении заявок");
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredRequests = useMemo(() => {
    if (!requests || !Array.isArray(requests)) return [];
    
    // Сортировка: сначала взятые в работу, потом доступные
    return requests.sort((a, b) => {
      const aTaken = !!a.counterparty_id;
      const bTaken = !!b.counterparty_id;
      
      if (aTaken && !bTaken) return -1;
      if (!aTaken && bTaken) return 1;
      return 0;
    });
  }, [requests]);

  // Вычисляем индексы для текущей страницы
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(
    indexOfFirstRequest,
    indexOfLastRequest,
  );
  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);


  // Новая функция для определения статуса на основе counterparty_id
  const getCounterpartyStatus = (request: Request) => {
    if (!request.counterparty_id) {
      return "Открыто";
    }
    return "В работе";
  };

  const getCounterpartyStatusColor = (request: Request) => {
    if (!request.counterparty_id) {
      return "bg-blue-100 text-blue-800";
    }
    return "bg-yellow-100 text-yellow-800";
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return formatInTimeZone(
      new Date(dateStr),
      "Europe/Moscow",
      "dd.MM.yyyy HH:mm:ss",
    );
  };

  const handleTakeRequest = (requestId: number) => {
    setSelectedRequestId(requestId);
    setConfirmTakeOpen(true);
  };

  const handleConfirmTake = async () => {
    if (!selectedRequestId || isTakingRequest) return;
    
    setIsTakingRequest(true);
    try {
      await counterpartyApi.takeRequest(selectedRequestId);
      logger.info("Request taken:", selectedRequestId);
      fetchRequests(); // Обновляем список
      setConfirmTakeOpen(false);
      setSelectedRequestId(null);
    } catch (err: unknown) {
      logger.error("Error taking request:", err);
      setError("Ошибка при взятии заявки в работу");
    } finally {
      setIsTakingRequest(false);
    }
  };

  const handleConfirmRequest = (requestId: number) => {
    setSelectedRequestId(requestId);
    setConfirmApproveOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!selectedRequestId || isApprovingRequest) return;
    
    setIsApprovingRequest(true);
    try {
      await counterpartyApi.confirmRequest(selectedRequestId);
      logger.info("Request confirmed:", selectedRequestId);
      fetchRequests(); // Обновляем список
      setConfirmApproveOpen(false);
      setSelectedRequestId(null);
    } catch (err: unknown) {
      logger.error("Error confirming request:", err);
      setError("Ошибка при подтверждении заявки");
    } finally {
      setIsApprovingRequest(false);
    }
  };

  const handleRejectRequest = (request: Request) => {
    setSelectedRequestForReject(request);
    setRejectWidgetOpen(true);
  };

  const handleRejectWidgetClose = () => {
    setRejectWidgetOpen(false);
    setSelectedRequestForReject(null);
  };

  const handleRequestRejected = () => {
    fetchRequests(); // Обновляем список
  };

  const handleConfirmTakeClose = () => {
    setConfirmTakeOpen(false);
    setSelectedRequestId(null);
    setIsTakingRequest(false);
  };

  const handleConfirmApproveClose = () => {
    setConfirmApproveOpen(false);
    setSelectedRequestId(null);
    setIsApprovingRequest(false);
  };

  const handleCopyCardNumber = async (cardNumber: string) => {
    try {
      await navigator.clipboard.writeText(cardNumber);
      logger.info("Card number copied to clipboard:", cardNumber);
    } catch (err: unknown) {
      logger.error("Error copying card number:", err);
      setError("Ошибка при копировании номера карты");
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* Заголовок с кнопкой выхода */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-center items-center">
            <button
              onClick={() => {
                localStorage.removeItem('counterparty_token');
                localStorage.removeItem('counterparty');
                window.location.href = '/counterparty/login';
              }}
              className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Выйти
            </button>
          </div>
          
          {/* Загрузка */}
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* Заголовок с кнопкой выхода */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-center items-center">
            <button
              onClick={() => {
                localStorage.removeItem('counterparty_token');
                localStorage.removeItem('counterparty');
                window.location.href = '/counterparty/login';
              }}
              className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Выйти
            </button>
          </div>
          
          {/* Ошибка */}
          <div className="p-6">
            <div
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative"
              role="alert"
            >
              <strong className="font-bold">Ошибка</strong>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // console.log('RequestsTable: Rendering, loading:', loading, 'error:', error, 'requests:', requests);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
      {/* Заголовок с кнопкой выхода */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-center items-center">
        <button
          onClick={() => {
            localStorage.removeItem('counterparty_token');
            localStorage.removeItem('counterparty');
            window.location.href = '/counterparty/login';
          }}
          className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Выйти
        </button>
      </div>
      
      <div className="flex">
        <div className="flex-1 overflow-x-auto">
          {currentRequests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Пусто</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="divide-x divide-gray-100">
                  <th
                    scope="col"
                    className="px-6 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider rounded-tl-lg bg-slate-800"
                  >
                    Действия
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-slate-800"
                  >
                    ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-slate-800"
                  >
                    Сумма
                  </th>
                       <th
                         scope="col"
                         className="px-6 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-slate-800"
                       >
                         Банк
                       </th>
                       <th
                         scope="col"
                         className="px-6 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-slate-800"
                       >
                         Получатель
                       </th>
                       <th
                         scope="col"
                         className="px-6 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-slate-800"
                       >
                         Карта
                       </th>
                       <th
                         scope="col"
                         className="px-6 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-slate-800"
                       >
                         Статус
                       </th>
                       <th
                         scope="col"
                         className="px-6 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider rounded-tr-lg bg-slate-800"
                       >
                         Создано
                       </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-50">
                {currentRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-gray-50 divide-x divide-gray-50 transition-colors duration-150"
                  >
                         <td className="px-6 py-[15.5px] whitespace-nowrap text-sm text-gray-500">
                           <div className="w-full space-y-1">
                             {!request.counterparty_id ? (
                               <button
                                 onClick={() => handleTakeRequest(request.id)}
                                 className="w-full text-blue-600 hover:text-blue-800 text-xs px-2 py-1 bg-blue-100 rounded"
                               >
                                 Взять
                               </button>
                             ) : (
                               <div className="flex space-x-1">
                                 <button
                                   onClick={() => handleConfirmRequest(request.id)}
                                   className="flex-1 text-green-600 hover:text-green-800 text-xs px-2 py-1 bg-green-100 rounded"
                                 >
                                   Апрув
                                 </button>
                                 <button
                                   onClick={() => handleRejectRequest(request)}
                                   className="flex-1 text-red-600 hover:text-red-800 text-xs px-2 py-1 bg-red-100 rounded"
                                 >
                                   Отмена
                                 </button>
                               </div>
                             )}
                           </div>
                         </td>
                    <td className="px-6 py-[15.5px] whitespace-nowrap text-sm font-medium text-gray-900">
                      {request.id}
                    </td>
                    <td className="px-6 py-[15.5px] whitespace-nowrap text-sm text-gray-500">
                      {request.counterparty_id ? (
                        <span className="text-gray-900 font-medium">{request.amount} ₽</span>
                      ) : (
                        <span className="blur-sm select-none">•••• ₽</span>
                      )}
                    </td>
                    <td className="px-6 py-[15.5px] whitespace-nowrap text-sm text-gray-500">
                      {request.bank_name}
                    </td>
                    <td className="px-6 py-[15.5px] whitespace-nowrap text-sm text-gray-500">
                      {request.counterparty_id ? (
                        <span className="text-gray-900">{request.card_holder || '-'}</span>
                      ) : (
                        <span className="blur-sm select-none">••••••••</span>
                      )}
                    </td>
                    <td className="px-6 py-[15.5px] whitespace-nowrap text-sm text-gray-500">
                      {request.counterparty_id ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-900 font-mono">{request.card_number || '-'}</span>
                          {request.card_number && (
                            <button
                              onClick={() => handleCopyCardNumber(request.card_number!)}
                              className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                              title="Копировать номер карты"
                            >
                              📋
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="blur-sm select-none">••••••••</span>
                      )}
                    </td>
                    <td className="px-6 py-[15.5px] whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getCounterpartyStatusColor(request)}`}
                      >
                        {getCounterpartyStatus(request)}
                      </span>
                    </td>
                    <td className="px-6 py-[15.5px] whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(request.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Показано {indexOfFirstRequest + 1}-{Math.min(indexOfLastRequest, filteredRequests.length)} из {filteredRequests.length} заявок
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Назад
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Страница {currentPage} из {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Вперед
              </button>
            </div>
          </div>
        </div>
           )}
           </div>

           {/* Виджет отмены заявки */}
           <RejectRequestWidget
             isOpen={rejectWidgetOpen}
             onClose={handleRejectWidgetClose}
             request={selectedRequestForReject}
             onRequestRejected={handleRequestRejected}
           />

           {/* Виджет подтверждения взятия в работу */}
           <ConfirmTakeWidget
             isOpen={confirmTakeOpen}
             onClose={handleConfirmTakeClose}
             onConfirm={handleConfirmTake}
             requestId={selectedRequestId || 0}
           />

           {/* Виджет подтверждения апрува */}
           <ConfirmApproveWidget
             isOpen={confirmApproveOpen}
             onClose={handleConfirmApproveClose}
             onConfirm={handleConfirmApprove}
             requestId={selectedRequestId || 0}
             isProcessing={isApprovingRequest}
           />
         </div>
       );
     };

     export default RequestsTable;
